/**
 * Seattle Metro Solo - Phase 5: 商品輸送 Move
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_05_move_goods_seattle.md
 */

import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { SeattleGameState, MoveGoodsSeattleArgs, GoodsColor } from '../../types';
import { validateMoveGoodsSeattle, isGrayCity } from '../validators/moveGoodsSeattleValidator';
import { calculateExpectedIncome } from '../utils/pathfinder';

/**
 * 商品輸送 Move（Seattle固有）
 *
 * @param G - Seattle ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - 商品輸送引数
 * @returns void or INVALID_MOVE
 */
export function moveGoodsSeattle(
  { G, ctx }: { G: SeattleGameState; ctx: Ctx },
  args: MoveGoodsSeattleArgs
): void | typeof INVALID_MOVE {
  // バリデーション
  const validation = validateMoveGoodsSeattle(G, ctx, args);
  if (!validation.success) {
    console.error(`[moveGoodsSeattle] Validation failed: ${validation.error} - ${validation.message}`);
    return INVALID_MOVE;
  }

  const { fromCityId, toCityId, goodsColor, grayCityAction, path } = args;
  const player = G.players['0'];

  // 1. 出発地点から商品を削除
  removeGoodsFromSource(G, fromCityId, goodsColor);

  // 2. 収入計算（共通関数を使用：Gray City は0、通常はリンク数）
  const income = path ? calculateExpectedIncome(G as any, toCityId, path) : 0;
  player.incomeLevel += income;

  // 3. 目的地への処理
  if (isGrayCity(toCityId)) {
    // Gray City への輸送
    if (grayCityAction === 'deliver') {
      // 取り除く: deliveredCubes +1、商品はゲームから除去
      player.deliveredCubes += 1;
      G.removedCubes.push(goodsColor);
      debug.log(`[moveGoodsSeattle] Delivered ${goodsColor} to ${toCityId}, total: ${player.deliveredCubes}`);
    } else {
      // 留置する: Gray City の hex.goods に配置
      const destHex = G.map.find(h => h.city?.id === toCityId);
      if (destHex) {
        destHex.goods.push(goodsColor);
        debug.log(`[moveGoodsSeattle] Kept ${goodsColor} at ${toCityId}, total: ${destHex.goods.length}`);
      }
    }
  } else {
    // 通常 City への輸送
    G.removedCubes.push(goodsColor);
    debug.log(`[moveGoodsSeattle] Delivered ${goodsColor} to ${toCityId}, income +${income}`);
  }

  // 4. 輸送回数を記録（共通カウンタを使用）
  player.turnsCompletedThisPhase += 1;

  debug.log(`[moveGoodsSeattle] Deliveries this turn: ${player.turnsCompletedThisPhase}/${G.maxDeliveriesThisPhase}`);
}

/**
 * 出発地点から商品を削除
 *
 * Gray City も含め、全ての City/Town は hex.goods で管理されます。
 */
function removeGoodsFromSource(G: SeattleGameState, fromCityId: string, goodsColor: GoodsColor): void {
  // マップ上の Hex から削除（Gray City も同様に処理）
  const hex = G.map.find(h => h.city?.id === fromCityId || h.town?.id === fromCityId);
  if (hex && hex.goods) {
    const index = hex.goods.indexOf(goodsColor);
    if (index !== -1) {
      hex.goods.splice(index, 1);
    }
  }
}

// passMoveGoodsSeattle は廃止: 共通 passMoveGoods を使用
// Issue #9 + Technical Debt #1: Phase 5 輸送回数の共通化
